/**
 * Defensive prop coercion helpers — shared across every block that
 * reads a prop whose runtime shape might not match what the component
 * expects. Direct port of ThemeMaker's
 * {@code lms-portal/src/lib/platform/coerce.ts}.
 *
 * <h3>Why these exist (and why they're separate from zod)</h3>
 * <ul>
 *   <li><b>zod is a gate</b>: parse this payload, throw if it doesn't
 *       match. Right for API responses, where a malformed envelope
 *       is fatal.</li>
 *   <li><b>These helpers are coercers</b>: take whatever shape the
 *       prop happens to be, return the closest sensible value the
 *       component can render. Right for content authored by humans,
 *       where {@code interests: {en: "AI, ML"}} instead of
 *       {@code interests: ["AI", "ML"]} is a data-entry mistake we
 *       want to absorb rather than crash on.</li>
 * </ul>
 *
 * <p>Every helper returns a value of the declared type, ALWAYS. No
 * throws. The caller never has to write {@code (x ?? []).slice(...)}
 * or {@code typeof x === 'string' ? ...} again.
 */

/**
 * Coerce any value to a display string. Handles plain strings,
 * localized objects ({@code {en, tr, ...}} left over after
 * {@code localizeTree} couldn't resolve a locale), numbers, null,
 * and anything else. Empty for missing/null.
 */
export function tx(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && !Array.isArray(v)) {
    const obj = v as Record<string, unknown>;
    if (typeof obj['en'] === 'string' && obj['en'] !== '') return obj['en'];
    for (const k of Object.keys(obj)) {
      const val = obj[k];
      if (typeof val === 'string' && val !== '') return val;
    }
    return '';
  }
  return String(v);
}

/**
 * Coerce to array. Components routinely call {@code .map() / .slice()
 * / .filter()} on props the schema declares as arrays — but content
 * authors sometimes write {@code {en: "a, b, c"}} instead of
 * {@code ["a", "b", "c"]}. After {@code localizeTree} resolves the
 * localized object to a string, the renderer's {@code .map} crashes.
 * {@code asList} absorbs every shape:
 * <ul>
 *   <li>already an array → as-is</li>
 *   <li>non-empty string → split on commas/semicolons, trim, drop empties</li>
 *   <li>anything else → empty array</li>
 * </ul>
 */
export function asList<T = unknown>(v: unknown): T[] {
  if (Array.isArray(v)) return v as T[];
  if (typeof v === 'string') {
    return v
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean) as unknown as T[];
  }
  return [];
}

/**
 * Coerce to finite number with a required fallback. Handles:
 * actual numbers (including 0), numeric strings, null / undefined /
 * NaN / Infinity → fallback. The fallback is required (no overload)
 * so the call site is explicit about what "missing" should mean.
 */
export function asNumber(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

/**
 * Coerce to boolean with a fallback. Handles {@code "true" / "false"
 * / "1" / "0" / "yes" / "no"} strings + actual booleans + numbers —
 * the empty string is falsy in raw coercion, but the literal
 * {@code "false"} string is truthy. Without this helper that's a
 * recurring bug class.
 */
export function asBool(v: unknown, fallback = false): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no' || s === '') return false;
  }
  if (typeof v === 'number') return v !== 0;
  return fallback;
}

/**
 * Coerce to plain object. Use for nested-config props
 * ({@code theme}, {@code actions}, {@code meta}) where the block
 * expects to read named fields. Anything other than a plain object
 * returns an empty object — the block then reads optional fields
 * with {@code ??} defaults and renders cleanly. Arrays explicitly
 * DON'T pass.
 */
export function asObject(v: unknown): Record<string, unknown> {
  if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
  return {};
}

/**
 * Pick a value from a closed set, falling back if it's not in the set.
 * Stricter than {@code tx} — guarantees the returned value is one of
 * the allowed options. Helps blocks avoid {@code if (variant === 'unknown')}
 * branches scattered everywhere.
 */
export function pickEnum<T extends string>(v: unknown, options: readonly T[], fallback: T): T {
  if (typeof v === 'string' && (options as readonly string[]).includes(v)) return v as T;
  return fallback;
}
